import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import config from '../../../../config';
import cycleActions from '../../../../redux/actions/cycleActions';
import { RootState } from '../../../../redux/store';
import { getApprovedDevicePrettyName, getPlatformPrettyName } from '../../../../services/deviceService';
import { CycleStatus } from '../../../../types/CycleStatus';
import { TesterStatusInCycle } from '../../../../types/TesterStatusInCycle';
import { UserStatus } from '../../../../types/UserStatus';
import { WorkCategory } from '../../../../types/WorkCategory';
import { DefinitionList, Notice, PageContainer, PageSection } from '../../../../ui';

const workCategoryLabel = {
  [WorkCategory.FeedbackAndRating]: 'Get feedback and star rating',
  [WorkCategory.FindBugsExploratory]: 'Find bugs - exploratory',
  [WorkCategory.FindBugsTestPlan]: 'Find bugs - test plan',
};

const ProceedButton: React.FC<{ to: string; disabled?: boolean }> = ({ to, disabled, children }) => (
  <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} component={RouterLink} to={to} disabled={disabled}>
    {children}
  </Button>
);

/* The next step for this cycle: apply (device selection), view the bid, or why not. */
const ProceedToDeviceSelectionButton = ({ userStatus, cycle }) => {
  const applyPath = `/account/tester/cycle/${cycle.id}/apply`;

  if (cycle.id === config.evalCycleId && !cycle.testerStatus) {
    return <ProceedButton to={applyPath}>Proceed to device selection</ProceedButton>;
  }

  const isRejected =
    cycle.testerStatus === TesterStatusInCycle.Rejected ||
    (typeof cycle.testerStatus === 'undefined' && cycle.status === CycleStatus.Testing);

  if (userStatus === UserStatus.Active) {
    if (isRejected) {
      return <ProceedButton to={applyPath} disabled>Bid closed</ProceedButton>;
    }
    if (cycle.testerStatus === TesterStatusInCycle.Pending) {
      return <ProceedButton to={applyPath}>Proceed to view my bid</ProceedButton>;
    }
    return <ProceedButton to={applyPath}>Proceed to device selection</ProceedButton>;
  }

  if (userStatus === UserStatus.EvalCyclePending) {
    if (cycle.testerStatus === TesterStatusInCycle.Rejected) {
      return <ProceedButton to={applyPath} disabled>Bid closed</ProceedButton>;
    }
    return (
      <Tooltip title="You should complete the test cycle for new testers and get a rating from the Testpoint team first">
        <span>
          <Button variant="outlined" size="large" endIcon={<ArrowForwardIcon />} disabled>
            Proceed to device selection
          </Button>
        </span>
      </Tooltip>
    );
  }

  return null;
};

/* Tester → cycle → Cycle details */
const CycleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const cycleId = parseInt(id, 10);
  const dispatch = useDispatch();
  const { cycles, user, resolution } = useSelector((state: RootState) => state);
  const isMobile = resolution.device === 'mobile' || resolution.device === 'mobile-tablet';

  useEffect(() => {
    if (!(cycles as any[]).length) {
      dispatch(cycleActions.listCycles());
    }
  }, []);

  const cycle: any = (cycles as any[]).find((el) => el.id === cycleId);

  if (!cycle) {
    return null;
  }

  const hasDevice = [TesterStatusInCycle.Approved, TesterStatusInCycle.AwaitingReview, TesterStatusInCycle.Finished].includes(cycle.testerStatus);
  const platformNames = cycle.platformUrls.map((el) => getPlatformPrettyName(el.platformId)).join(', ');
  const deviceNames = cycle.testEnvs.filter((el) => el.type !== 'Platform').map((el) => el.name).join(', ');

  return (
    <PageContainer isMobile={isMobile}>
      {user.status === UserStatus.EvalCyclePending && !cycle.testerStatus && (
        <Notice title="Please review the test cycle details below">
          Use the tabs above for more details. To accept this test cycle, click &quot;Proceed to device selection&quot;, choose the
          devices or browsers you will use, and then click &quot;Accept&quot;.
        </Notice>
      )}
      {cycle.status === CycleStatus.Completed && (
        <Notice title="This cycle has finished">
          <Link component={RouterLink} to="/account/tester">Go to dashboard</Link>
        </Notice>
      )}

      <PageSection title="Cycle details" padding={isMobile ? 1.5 : 2.5}>
        {cycle.version && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: -1, mb: 1 }}>
            Version {cycle.version}
          </Typography>
        )}
        <DefinitionList
          isMobile={isMobile}
          items={[
            hasDevice && {
              label: 'Your chosen device',
              value: (
                <>
                  {getApprovedDevicePrettyName(cycle.approvedDevice)} <i>(you must test with this device)</i>
                </>
              ),
            },
            {
              label: 'Product description, testing guidelines',
              value: (
                // Customer-written HTML; headings toned down to body size
                <Box
                  sx={{ '& h1, & h2, & h3': { fontSize: 16, fontWeight: 600, m: '8px 0' }, '& h4, & h5, & h6': { fontSize: 14, fontWeight: 600, m: '6px 0' }, '& p': { m: '0 0 8px' } }}
                  dangerouslySetInnerHTML={{ __html: cycle.description }}
                />
              ),
            },
            { label: 'Customer name', value: cycle.ownerCompanyName },
            { label: 'Deliverable', value: workCategoryLabel[cycle.workCategory] },
            platformNames.length > 0 && { label: 'Platforms', value: platformNames },
            deviceNames.length > 0 && { label: 'Devices', value: deviceNames },
            cycle.testerStatus && {
              label: 'App/browser links',
              value: cycle.platformUrls.map((el, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 120 }}>{getPlatformPrettyName(el.platformId)}</Typography>
                  <Link href={el.url} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>{el.url}</Link>
                </Box>
              )),
            },
            { label: 'Priority for favorite testers', value: cycle.useFavoriteTesters ? 'Yes' : 'No' },
            { label: 'Requested hours', value: cycle.hoursPerTester },
            cycle.workCategory === WorkCategory.FeedbackAndRating && {
              label: 'Questions',
              value: (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {cycle.questions.map((el, i) => <li key={i}>{el}</li>)}
                </Box>
              ),
            },
          ]}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <ProceedToDeviceSelectionButton userStatus={user.status} cycle={cycle} />
        </Box>
      </PageSection>
    </PageContainer>
  );
};

export default CycleDetails;
